"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CnamBookModal from "../CnamBookModal/CnamBookModal";
import useAuthSession from "@/authPatient/hooks/useAuthSession";
import { fetchUserInsurances } from "@/Profile/redux/thunks/medicalThunks";
import { selectUserInsurances } from "@/Profile/redux/selectors/medicalSelectors";

import "./InsuranceCard.css";

const selectCurrentUser = (state) =>
  state.auth?.user ??
  state.patientAuth?.user ??
  state.doctorAuth?.user ??
  null;

export default function InsuranceCard({
  title,
  insurances, // optionnel: si fourni, prioritaire sur le store
  provider,
  policyNo,
  cardId,
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // Direction du texte selon la langue active (ar => rtl),
  // même logique que dans SpecialtyFilter.jsx / CreateCaseForm.jsx.
  const isRTL = i18n.dir(i18n.language) === "rtl";

  const currentUser = useSelector(selectCurrentUser);
  const { effectiveUserId, effectiveUser } = useAuthSession({ auto: true });

  const resolvedUser = currentUser || effectiveUser || null;
  const resolvedUserId = resolvedUser?.id || effectiveUserId || null;

  // ✅ Assurances stockées dans redux (mises à jour par le GET ici ET par le save du formulaire)
  const storedInsurances = useSelector(selectUserInsurances);

  const [loadingInsurances, setLoadingInsurances] = useState(true);
  const [openCnam, setOpenCnam] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(cardId ?? null);

  // ✅ GET des assurances de l'utilisateur au montage (et si l'id change)
  useEffect(() => {
    if (!resolvedUserId) {
      setLoadingInsurances(false);
      return;
    }

    let active = true;
    setLoadingInsurances(true);

    Promise.resolve(dispatch(fetchUserInsurances(resolvedUserId)))
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingInsurances(false);
      });

    return () => {
      active = false;
    };
  }, [dispatch, resolvedUserId]);

  // Priorité aux insurances passées en prop, sinon on prend celles du store
  const effectiveInsurances = useMemo(() => {
    if (Array.isArray(insurances) && insurances.length > 0) {
      return insurances;
    }
    return Array.isArray(storedInsurances) ? storedInsurances : [];
  }, [insurances, storedInsurances]);

  const displayTitle = title || t("insurance.card.title", { defaultValue: "Medical Insurance" });

  const displayList = useMemo(() => {
    if (effectiveInsurances.length > 0) {
      return effectiveInsurances.map((ins, idx) => ({
        id: ins?.id ?? idx,
        label: ins?.label ?? ins,
        provider: ins?.provider ?? ins?.label ?? ins,
        policyNo: ins?.policyNo ?? policyNo ?? null,
        cardId: ins?.cardId ?? cardId ?? null,
        identifiant: ins?.identifiant ?? ins?.policyNo ?? policyNo ?? null,
      }));
    }

    if (provider) {
      return [
        {
          id: 0,
          label: `${provider}${policyNo ? ` — ${policyNo}` : ""}`,
          provider,
          policyNo: policyNo ?? null,
          cardId: cardId ?? null,
          identifiant: policyNo ?? null,
        },
      ];
    }

    return [];
  }, [effectiveInsurances, provider, policyNo, cardId]);

  const isCnam = (value = "") =>
    typeof value === "string" &&
    (value.toLowerCase().includes("cnam") ||
      value.toLowerCase().includes("national health insurance"));

  const handleInsuranceClick = (ins) => {
    if (!isCnam(ins.label) && !isCnam(ins.provider)) return;

    setSelectedCardId(ins.cardId ?? selectedCardId ?? null);
    setOpenCnam(true);
  };

  const handleCardCreated = (createdCard) => {
    if (createdCard?.id) {
      setSelectedCardId(createdCard.id);
    }
  };

  return (
    <>
      <div className="ins-card" dir={isRTL ? "rtl" : "ltr"}>
        <div className="ins-icon" aria-hidden />
        <h3 className="ins-title">{displayTitle}</h3>

        {loadingInsurances && displayList.length === 0 ? (
          <p className="ins-sub">{t("insurance.card.loading", { defaultValue: "Chargement..." })}</p>
        ) : displayList.length === 0 ? (
          <p className="ins-sub">{t("insurance.card.empty", { defaultValue: "No insurance found" })}</p>
        ) : (
          <ul className="ins-list">
            {displayList.map((ins) => {
              const clickable = isCnam(ins.label) || isCnam(ins.provider);

              return (
                <li
                  key={ins.id}
                  className={`ins-list__item ${
                    clickable ? "ins-list__item--clickable" : ""
                  }`}
                  onClick={() => handleInsuranceClick(ins)}
                >
                  {ins.label}
                </li>
              );
            })}
          </ul>
        )}

        <button
          type="button"
          className="ins-card__button"
          onClick={() => setOpenCnam(true)}
        >
          {t("insurance.card.viewDetails", { defaultValue: "View details" })}
        </button>
      </div>

      <CnamBookModal
        open={openCnam}
        onClose={() => setOpenCnam(false)}
        cardId={selectedCardId}
        userId={resolvedUserId}
        onCardCreated={handleCardCreated}
      />
    </>
  );
}